/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OrganisationUnit } from '@app/models';
import { toFormattedRate } from '@app/utilities';
import {
  useCreateAccount,
  useGetProduct,
  useOrganisationUnitsWithProducts,
  useProductsByOuId,
  useProductConfig,
  useGetAvailableDays,
} from '@app/sdk';
import { useAddTenantAccountId } from '../../../GlobalContextProvider';
import { FormProvider, useForm } from 'react-hook-form';
import { FormContainer, Input, Select, SelectOption } from '@app/shared';
import { AccountIdContext } from '../AccountIdContext';
import { creditLimitIncreasePreferenceOptions } from '../constants/credit-limit-increase-preference-options';
import { toast } from 'react-toastify';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import isValid from 'date-fns/isValid';
import set from 'date-fns/set';
import subDays from 'date-fns/subDays';
import AccountCreationDateSelector from './AccountCreationDateSelector';

type AccountModel = {
  accountId: string;
  creditLimit: number;
  creditLimitIncreasePreference: string;
  productId: string;
  ratesToUse: any;
};

type Rate = {
  subledgerType: string;
  spendType: string;
  rate: number;
  feeCategory: string;
  shouldMatch: boolean;
  temporaryRate: number;
};

const maxDaysInPast = 10000;

export default function CreateAccount() {
  const navigate = useNavigate();
  const { setAccountId, setBillingDate, setPaymentDueDateStrategy } = useContext(AccountIdContext);

  const [selectedOrganisationUnitId, setSelectedOrganisationUnitId] = useState<string>();
  const [productId, setProductId] = useState<string>(undefined as any);
  const [paymentDay, setPaymentDay] = useState<string | null>(null);
  const [statementDay, setStatementDay] = useState<string | null>(null);
  const [paymentOrStatementDay, setPaymentOrStatementDay] = useState<string>('Statement Day');

  const [selectedAccountRates, setSelectedAccountRates] = useState(undefined as any);
  const [ratesToUse, setRatesToUse] = useState<Rate[]>([]);
  const addAccountId = useAddTenantAccountId();
  const [creationDate] = useState(() => new Date());
  const [effectiveDate, setEffectiveDate] = useState(creationDate);
  const [daysInPast, setDaysInPast] = useState('0');

  const { mutateAsync: createAccount, isPending: isCreating } = useCreateAccount();

  const { data: organisationUnits } = useOrganisationUnitsWithProducts();
  const { data: selectedProduct } = useGetProduct({ productId });
  const { data: products } = useProductsByOuId({ ouId: selectedOrganisationUnitId });
  const { data: productConfig } = useProductConfig({ productId });
  const { data: availableDays } = useGetAvailableDays({ productId });

  useEffect(() => {
    setAccountId(undefined);
  }, [setAccountId]);

  useEffect(() => {
    let ratesList = [];
    if (selectedProduct && selectedAccountRates) {
      const selectedAccountRatesAsObject = JSON.parse(selectedAccountRates);
      ratesList = selectedProduct.ratesList.find(
        (rate) => rate.effectiveDate == selectedAccountRatesAsObject.effectiveDate
      )?.availableRates[selectedAccountRatesAsObject.index].interestRates;
    }

    setRatesToUse(ratesList?.filter((rate) => rate.shouldMatch));
  }, [selectedAccountRates, selectedProduct]);

  const getDays = (): SelectOption[] => {
    if (availableDays) {
      const selector = paymentOrStatementDay === 'Statement Day' ? 'statementDay' : 'paymentDueDay';
      return availableDays
        .map((i) => ({
          value: i[selector],
          label: i[selector],
        }))
        .sort((x, y) => x.value - y.value);
    }
    return null;
  };

  const onCreateAccount = useCallback(
    async (account: AccountModel) => {
      if (daysInPast === '') {
        toast.error('Please enter a number of days in past or select an account creation date');
        return;
      }
      if (!ratesToUse || ratesToUse.length == 0) {
        toast.error('Please select a rate or use default rate');
        return;
      }
      const { accountId } = await createAccount({
        creditLimit: account.creditLimit,
        creditLimitIncreasePreference: account.creditLimitIncreasePreference?.length ? account.creditLimitIncreasePreference : null,
        productId: productId,
        ratesList: ratesToUse,
        paymentDay: paymentDay ? +paymentDay : null,
        statementDay: statementDay ? +statementDay : null,
      });

      localStorage.setItem(`${accountId}-billing-date`, effectiveDate.toISOString());
      setAccountId(accountId);
      addAccountId(accountId);
      setTimeout(() => {
        navigate(`/accounts/${accountId}/summary`);
      }, 200);
    },
    [addAccountId, createAccount, daysInPast, effectiveDate, navigate, productId, ratesToUse, setAccountId]
  );

  const formContext = useForm();

  const updateDaysInPast = (value: string) => {
    if (value === '') {
      setDaysInPast('');
      return;
    }

    const days = Number(value);
    if (!Number.isInteger(days) || days < 0 || days > maxDaysInPast) {
      return;
    }

    setDaysInPast(value);
    setEffectiveDate(subDays(creationDate, days));
  };

  const updateAccountCreationDate = (date: Date | null) => {
    if (!date) {
      return;
    }

    const days = differenceInCalendarDays(creationDate, date);
    if (!isValid(date) || days < 0 || days > maxDaysInPast) {
      toast.error('Account creation date must be within the past 10000 days');
      return;
    }

    setDaysInPast(String(days));
    setEffectiveDate(
      set(creationDate, {
        year: date.getFullYear(),
        month: date.getMonth(),
        date: date.getDate(),
      })
    );
  };

  useEffect(() => {
    setBillingDate(effectiveDate);
  }, [effectiveDate, setBillingDate]);

  useEffect(() => {
    if (productConfig) {
      const { paymentDueDateStrategy } = productConfig['consumer-billing']['cycleParameters'];
      if (paymentDueDateStrategy) {
        setPaymentOrStatementDay(paymentDueDateStrategy === 'Fixed' ? 'Payment Day' : 'Statement Day');
        setPaymentDueDateStrategy(paymentDueDateStrategy);
      }
    }
  }, [productConfig]);

  return (
    <section>
      <h1>Create Account</h1>
      <FormProvider {...formContext}>
        <FormContainer
          isSubmitting={isCreating}
          onSubmit={onCreateAccount}
          onReset={() => {
            setDaysInPast('0');
            setEffectiveDate(creationDate);
            setPaymentDay(null);
            setStatementDay(null);
          }}>
          <Input
            name={'creditLimit'}
            label={'Credit Limit'}
            min={1}
            type={'number'}
            defaultValue={''}
            placeholder={'Enter Credit Limit'}
            disabled={isCreating}
            options={{ required: 'Credit Limit is required' }}
          />          
          <Select
            name="creditLimitIncreasePreference"
            selectOptions={creditLimitIncreasePreferenceOptions}
            label="Credit Limit Increase Preference"
          />
          <Select
            name={'organisationUnitId'}
            label={'Organisation Unit'}
            placeholder={'Select an organisation unit'}
            defaultValue={''}
            onChange={(v) => setSelectedOrganisationUnitId(v.target.value)}
            selectOptions={
              organisationUnits?.map((ou: OrganisationUnit) => ({
                value: ou.id,
                label: ou.name,
              })) || []
            }
            options={{ required: 'Organisation Unit is required' }}
          />
          <Select
            name={'productId'}
            label={'Product'}
            placeholder={'Select a Product'}
            onChange={(v) => {
              setProductId(v.target.value);
            }}
            defaultValue={''}
            disabled={isCreating}
            options={{ required: 'Product is required' }}
            selectOptions={products?.map((p) => ({ value: p.productId, label: p.label })) || []}
          />
          <div className={`sixteen wide column`}>
            <div className="field">
              <div className="ui labeled input">
                <div className="ui label">Account Rate *</div>
                <select
                  className="ui fluid search dropdown "
                  onChange={(v) => setSelectedAccountRates(v.target.value)}
                  disabled={isCreating}>
                  <option value="">Select...</option>
                  {selectedProduct?.ratesList &&
                    selectedProduct.ratesList.map((rateList) => (
                      <optgroup
                        label={'Effective Date: ' + rateList.effectiveDate.substring(0, 10)}
                        key={rateList.effectiveDate}>
                        {rateList.availableRates.map((rate, index) => (
                          <option
                            value={JSON.stringify({ effectiveDate: rateList.effectiveDate, index })}
                            key={rateList.effectiveDate.substring(0, 10) + JSON.stringify(rate.interestRates)}>
                            {rate.interestRates
                              .map(
                                (ir, index) =>
                                  (ir.subledgerType ? ir.subledgerType : '') +
                                  (ir.spendType && !ir.subledgerType ? ir.spendType : '') +
                                  (ir.feeCategory  && !ir.subledgerType ? '/' + ir.feeCategory : '') +
                                  ': ' +
                                  toFormattedRate(ir.rate) +
                                  (selectedProduct?.rateTracking.find(
                                    (rt) => rt.spendType === ir.spendType && rt.feeCategory === ir.feeCategory && rt.subledgerType === ir.subledgerType
                                  )
                                    ? ' tracking ' +
                                      selectedProduct?.rateTracking.find(
                                        (rt) => rt.spendType === ir.spendType && rt.feeCategory === ir.feeCategory && rt.subledgerType === ir.subledgerType
                                      ).trackType
                                    : '') +
                                  (ir.temporaryRate != null
                                    ? ' w/ temp rate at ' + toFormattedRate(ir.temporaryRate)
                                    : '') +
                                  (index != rate.interestRates.length - 1 ? ' | ' : '')
                              )
                              .join('') + (rate.isHeadlineRate ? ' | (Headline rate)' : '')}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                </select>
              </div>
            </div>
          </div>
          <Select
            name={paymentOrStatementDay}
            label={paymentOrStatementDay}
            onChange={(v) => {
              if (paymentOrStatementDay === 'Statement Day') {
                setPaymentDay(null);
                setStatementDay(v.target.value);
              } else {
                setPaymentDay(v.target.value);
                setStatementDay(null);
              }
            }}
            defaultValue=""
            disabled={isCreating || !availableDays}
            options={{ required: 'Payment day is required' }}
            selectOptions={
              getDays() ?? Array.from({ length: 31 }, (_, i) => i + 1).map((i) => ({ value: i, label: i }))
            }
          />
          <div className="sixteen wide column">
            <AccountCreationDateSelector
              baseDate={creationDate}
              creationDate={effectiveDate}
              daysInPast={daysInPast}
              maxDaysInPast={maxDaysInPast}
              onDaysInPastChange={updateDaysInPast}
              onDateChange={updateAccountCreationDate}
              disabled={isCreating}
            />
          </div>
        </FormContainer>
      </FormProvider>
    </section>
  );
}
